import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaFlask, FaPaperPlane, FaSyncAlt, FaXRay } from "react-icons/fa";
import Header from "../../../components/IntranetHeader";
import Footer from "../../../components/Footer";
import { supabase } from "../../../supabaseClient";
import { formatProfileName } from "../../../utils/profileName";

function formatDateTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const REFERRAL_STATUSES = ["Submitted", "Accepted", "In review", "Seen", "Declined", "Completed"];
const TEST_STATUSES = ["Requested", "Collected", "In progress", "Resulted", "Reviewed", "Cancelled"];
const RESULT_FLAGS = ["", "Normal", "Abnormal", "Critical"];

export default function ClinicalWorklist({ session, profile }) {
  const [referrals, setReferrals] = useState([]);
  const [tests, setTests] = useState([]);
  const [radiologyStudies, setRadiologyStudies] = useState([]);
  const [staff, setStaff] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [testDrafts, setTestDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    const [referralResult, testResult, radiologyResult, staffResult] = await Promise.all([
      supabase
        .from("clinical_referrals")
        .select("*, patient:patients(id, full_name, roleplay_nhs_number, roblox_username)")
        .order("created_at", { ascending: true }),
      supabase
        .from("diagnostic_requests")
        .select("*, patient:patients(id, full_name, roleplay_nhs_number, roblox_username)")
        .order("requested_at", { ascending: true }),
      supabase
        .from("radiology_studies")
        .select("id, diagnostic_request_id, modality, body_part, laterality, projection, generation_status, report_status, study_url, generated_at")
        .order("created_at", { ascending: true }),
      supabase
        .from("profiles")
        .select("id, full_name, name_prefix, position, suspended")
        .in("position", ["Staff", "Management"])
        .order("full_name", { ascending: true }),
    ]);

    if (referralResult.error || testResult.error || radiologyResult.error) {
      const workflowError = referralResult.error || testResult.error || radiologyResult.error;
      console.error("Unable to load clinical worklist:", workflowError);
      setError("Clinical workflow data could not be loaded. Run the required eCAMIS migrations, including ADD_ECAMIS_AI_RADIOLOGY.sql.");
    }

    setReferrals(referralResult.data || []);
    setTests(testResult.data || []);
    setRadiologyStudies(radiologyResult.data || []);
    setStaff((staffResult.data || []).filter((person) => person.suspended !== true));

    setDrafts((current) => {
      const next = { ...current };
      (referralResult.data || []).forEach((row) => {
        if (!next[row.id]) {
          next[row.id] = {
            status: row.status || "Submitted",
            assigned_to: row.assigned_to || "",
            response_notes: row.response_notes || "",
          };
        }
      });
      return next;
    });

    setTestDrafts((current) => {
      const next = { ...current };
      (testResult.data || []).forEach((row) => {
        if (!next[row.id]) {
          next[row.id] = {
            status: row.status || "Requested",
            result_flag: row.result_flag || "",
            result_summary: row.result_summary || "",
            patient_visible: row.patient_visible === true,
          };
        }
      });
      return next;
    });

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function saveReferral(row) {
    const draft = drafts[row.id] || {};
    setWorkingId(`ref-${row.id}`);
    setError("");

    const completed = ["Completed", "Declined", "Seen"].includes(draft.status);
    const { error: updateError } = await supabase
      .from("clinical_referrals")
      .update({
        status: draft.status,
        assigned_to: draft.assigned_to || null,
        response_notes: draft.response_notes || null,
        completed_at: completed ? new Date().toISOString() : null,
      })
      .eq("id", row.id);

    if (updateError) {
      setError(updateError.message || "The referral could not be updated.");
    } else {
      setMessage("Referral updated.");
      await load();
    }
    setWorkingId(null);
  }

  async function saveTest(row) {
    const draft = testDrafts[row.id] || {};
    setWorkingId(`test-${row.id}`);
    setError("");

    const now = new Date().toISOString();
    const resulted = ["Resulted", "Reviewed"].includes(draft.status);
    const reviewed = draft.status === "Reviewed";

    const { error: updateError } = await supabase
      .from("diagnostic_requests")
      .update({
        status: draft.status,
        result_flag: draft.result_flag || null,
        result_summary: draft.result_summary || null,
        resulted_by: resulted ? session?.user?.id || null : row.resulted_by,
        resulted_at: resulted ? row.resulted_at || now : null,
        reviewed_at: reviewed ? now : row.reviewed_at,
        patient_visible: draft.patient_visible === true,
      })
      .eq("id", row.id);

    if (updateError) {
      setError(updateError.message || "The test request could not be updated.");
      setWorkingId(null);
      return;
    }

    if (resulted && row.portal_account_id && draft.result_summary) {
      const alreadyResulted = ["Resulted", "Reviewed"].includes(row.status);
      if (!alreadyResulted) {
        await supabase.from("patient_messages").insert({
          portal_account_id: row.portal_account_id,
          patient_id: row.patient_id,
          subject: `${row.test_type} result update`,
          category: "Results",
          body: `A result has been recorded for your ${row.test_type}. Please view your patient portal for the latest update.`,
          created_by: session?.user?.id || null,
          action_url: "/patient-portal",
        });
      }
    }

    setMessage("Diagnostic request updated.");
    await load();
    setWorkingId(null);
  }

  const radiologyByDiagnosticId = new Map(
    radiologyStudies.map((study) => [study.diagnostic_request_id, study]),
  );

  const openReferrals = referrals.filter((row) => !["Completed", "Declined"].includes(row.status));
  const openTests = tests.filter((row) => !["Reviewed", "Cancelled"].includes(row.status));

  return (
    <div className="flex flex-col min-h-screen">
      <Header session={session} profile={profile} />
      <main className="nhsuk-width-container pguh-main">
        <div className="pguh-section-heading">
          <div>
            <span className="pguh-eyebrow">eCAMIS</span>
            <h1 className="nhsuk-heading-l">Clinical worklist</h1>
            <p className="nhsuk-body-m">Manage referrals, investigations and results across active patient care.</p>
          </div>
          <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={load} disabled={loading}>
            <FaSyncAlt aria-hidden="true" /> {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {message && <div className="pguh-timesheet-message pguh-timesheet-message--success">{message}</div>}
        {error && <div className="pguh-timesheet-message pguh-timesheet-message--error">{error}</div>}

        <section className="pguh-worklist-summary">
          <div><span>Open referrals</span><strong>{openReferrals.length}</strong></div>
          <div><span>Outstanding tests</span><strong>{openTests.length}</strong></div>
          <div><span>Critical results</span><strong>{tests.filter((row) => row.result_flag === "Critical" && row.status !== "Reviewed").length}</strong></div>
        </section>

        <section className="pguh-worklist-section">
          <div className="pguh-worklist-section__header">
            <div><FaPaperPlane aria-hidden="true" /><h2 className="nhsuk-heading-m">Referrals</h2></div>
            <span>{openReferrals.length} open</span>
          </div>

          {openReferrals.length === 0 ? (
            <div className="pguh-empty-state"><h3>No open referrals</h3><p>New referrals created from patient records will appear here.</p></div>
          ) : (
            <div className="pguh-worklist-grid">
              {openReferrals.map((row) => {
                const draft = drafts[row.id] || {};
                return (
                  <article key={row.id} className="pguh-worklist-card">
                    <div className="pguh-worklist-card__title">
                      <div>
                        <span className={`pguh-clinical-priority pguh-clinical-priority--${String(row.priority || "routine").toLowerCase()}`}>{row.priority}</span>
                        <h3>{row.referred_to}</h3>
                      </div>
                      <Link to={`/intranet/ecamis/patient/${row.patient_id}`}>Open patient</Link>
                    </div>
                    <p><strong>{row.patient?.full_name || "Patient"}</strong>{row.patient?.roblox_username ? ` · @${row.patient.roblox_username}` : ""}</p>
                    <p>{row.reason}</p>
                    <small>Submitted {formatDateTime(row.created_at)}</small>

                    <div className="pguh-worklist-card__form">
                      <label className="nhsuk-label">Status</label>
                      <select className="nhsuk-select" value={draft.status || "Submitted"} onChange={(event) => setDrafts((current) => ({ ...current, [row.id]: { ...draft, status: event.target.value } }))}>
                        {REFERRAL_STATUSES.map((status) => <option key={status}>{status}</option>)}
                      </select>
                      <label className="nhsuk-label">Assigned clinician</label>
                      <select className="nhsuk-select" value={draft.assigned_to || ""} onChange={(event) => setDrafts((current) => ({ ...current, [row.id]: { ...draft, assigned_to: event.target.value } }))}>
                        <option value="">Unassigned</option>
                        {staff.map((person) => <option key={person.id} value={person.id}>{formatProfileName(person)}</option>)}
                      </select>
                      <label className="nhsuk-label">Response / outcome</label>
                      <textarea className="nhsuk-textarea" rows={3} value={draft.response_notes || ""} onChange={(event) => setDrafts((current) => ({ ...current, [row.id]: { ...draft, response_notes: event.target.value } }))} />
                      <button className="nhsuk-button" type="button" disabled={workingId === `ref-${row.id}`} onClick={() => saveReferral(row)}>Save referral</button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="pguh-worklist-section">
          <div className="pguh-worklist-section__header">
            <div><FaFlask aria-hidden="true" /><h2 className="nhsuk-heading-m">Tests & results</h2></div>
            <span>{openTests.length} outstanding</span>
          </div>

          {openTests.length === 0 ? (
            <div className="pguh-empty-state"><h3>No outstanding tests</h3><p>New test requests created from patient records will appear here.</p></div>
          ) : (
            <div className="pguh-worklist-grid">
              {openTests.map((row) => {
                const draft = testDrafts[row.id] || {};
                const imaging = radiologyByDiagnosticId.get(row.id);
                return (
                  <article key={row.id} className={`pguh-worklist-card${row.result_flag === "Critical" ? " pguh-worklist-card--critical" : ""}${imaging ? " ruht-worklist-imaging-card" : ""}`}>
                    <div className="pguh-worklist-card__title">
                      <div>
                        <span className={`pguh-clinical-priority pguh-clinical-priority--${String(row.priority || "routine").toLowerCase()}`}>{row.priority}</span>
                        <h3>{row.test_type}</h3>
                      </div>
                      <Link to={`/intranet/ecamis/patient/${row.patient_id}`}>Open patient</Link>
                    </div>
                    <p><strong>{row.patient?.full_name || "Patient"}</strong></p>
                    {row.clinical_details && <p>{row.clinical_details}</p>}
                    <small>Requested {formatDateTime(row.requested_at)}</small>

                    {imaging ? (
                      <div className="ruht-worklist-imaging">
                        <div className="ruht-worklist-imaging__status">
                          <FaXRay aria-hidden="true" />
                          <div><strong>{imaging.generation_status}</strong><span>{imaging.report_status} · {imaging.laterality} {imaging.body_part}</span></div>
                        </div>
                        <p>This is an AI-generated Roblox imaging case. Open the study, inspect the image and write the radiology report there.</p>
                        <Link className="nhsuk-button" to={`/intranet/ecamis/imaging/${imaging.id}`}>Open imaging & report</Link>
                      </div>
                    ) : (
                      <div className="pguh-worklist-card__form">
                        <label className="nhsuk-label">Status</label>
                        <select className="nhsuk-select" value={draft.status || "Requested"} onChange={(event) => setTestDrafts((current) => ({ ...current, [row.id]: { ...draft, status: event.target.value } }))}>
                          {TEST_STATUSES.map((status) => <option key={status}>{status}</option>)}
                        </select>
                        <label className="nhsuk-label">Result flag</label>
                        <select className="nhsuk-select" value={draft.result_flag || ""} onChange={(event) => setTestDrafts((current) => ({ ...current, [row.id]: { ...draft, result_flag: event.target.value } }))}>
                          {RESULT_FLAGS.map((flag) => <option key={flag || "none"} value={flag}>{flag || "No flag"}</option>)}
                        </select>
                        <label className="nhsuk-label">Result / report</label>
                        <textarea className="nhsuk-textarea" rows={4} value={draft.result_summary || ""} onChange={(event) => setTestDrafts((current) => ({ ...current, [row.id]: { ...draft, result_summary: event.target.value } }))} />
                        <label className="pguh-inline-check">
                          <input
                            type="checkbox"
                            checked={draft.patient_visible === true}
                            onChange={(event) => setTestDrafts((current) => ({ ...current, [row.id]: { ...draft, patient_visible: event.target.checked } }))}
                          />
                          Share this result in the patient portal
                        </label>
                        <button className="nhsuk-button" type="button" disabled={workingId === `test-${row.id}`} onClick={() => saveTest(row)}>Save result</button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
